import { useCallback, useState } from 'react'
import type { MutationResult, QueryResult } from 'utils/api'
import { qs } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export interface SubscriptionTopic {
  id: string
  name: string
  description: string | null
  isDefault: boolean
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateSubscriptionTopicPayload {
  name: string
  description?: string | null
  isDefault?: boolean
}

export interface UpdateSubscriptionTopicPayload {
  name?: string
  description?: string | null
  isDefault?: boolean
  active?: boolean
}

export interface SubscriptionTopicsParams {
  includeInactive?: boolean
}

export function useSubscriptionTopics(params?: SubscriptionTopicsParams): QueryResult<SubscriptionTopic[]> {
  return useApiQuery<SubscriptionTopic[]>(`/api/subscription-topics${qs(params)}`)
}

export function useCreateSubscriptionTopic(): MutationResult<SubscriptionTopic, [CreateSubscriptionTopicPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: CreateSubscriptionTopicPayload) => {
      setLoading(true)
      try {
        return await fetchApi<SubscriptionTopic>('/api/subscription-topics', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateSubscriptionTopic(): MutationResult<
  SubscriptionTopic,
  [string, UpdateSubscriptionTopicPayload]
> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string, payload: UpdateSubscriptionTopicPayload) => {
      setLoading(true)
      try {
        return await fetchApi<SubscriptionTopic>(`/api/subscription-topics/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useDeleteSubscriptionTopic(): MutationResult<void, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string) => {
      setLoading(true)
      try {
        await fetchApi(`/api/subscription-topics/${id}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

// --- Per-contact subscriptions ---

export interface ContactSubscriptionEntry {
  topicId: string
  topicName: string
  topicDescription: string | null
  isDefault: boolean
  optedOutAt: string | null
  optedOutReason: string | null
}

export interface ContactSubscriptionsResponse {
  unsubscribedAt: string | null
  smsStoppedAt: string | null
  smsSenderStops: Array<{ senderPhone: string; nickname: string | null; optedOutAt: string }>
  subscriptions: ContactSubscriptionEntry[]
}

export function useContactSubscriptions(contactId: string | undefined): QueryResult<ContactSubscriptionsResponse> {
  return useApiQuery<ContactSubscriptionsResponse>(contactId ? `/api/contacts/${contactId}/subscriptions` : null)
}

export function useUpdateContactSubscription(): MutationResult<
  { subscriptions: ContactSubscriptionEntry[] },
  [string, { topicId: string; optedOut: boolean }]
> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (contactId: string, payload: { topicId: string; optedOut: boolean }) => {
      setLoading(true)
      try {
        return await fetchApi<{ subscriptions: ContactSubscriptionEntry[] }>(
          `/api/contacts/${contactId}/subscriptions`,
          { method: 'PUT', body: JSON.stringify(payload) },
        )
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateContactGlobalUnsubscribe(): MutationResult<
  { unsubscribedAt: string | null },
  [string, boolean]
> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (contactId: string, unsubscribed: boolean) => {
      setLoading(true)
      try {
        return await fetchApi<{ unsubscribedAt: string | null }>(`/api/contacts/${contactId}/unsubscribe-global`, {
          method: 'PUT',
          body: JSON.stringify({ unsubscribed }),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
